import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { Observable } from "rxjs";
import { AuthService } from "../services";

export const AuthGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree => {

    const router: Router = inject(Router);
    const authService: AuthService = inject(AuthService);

    if (authService.isLoggedIn()) {
        return true;
    }

    if (authService.isSsoEnabled) {
        if (authService.hasSsoAccount()) {
            // Signed in with Entra but the profile failed to load because the API ITSELF was
            // unreachable (deploy restart / cold start) - the sign-in is still valid, so go to the
            // reconnecting page, which polls the API and resumes the session automatically. Never
            // send this case to access-denied: that reads as "logged out" and forces a pointless
            // re-sign-in after every API deploy.
            if (authService.profileLoadFailure === 'unreachable') {
                return router.createUrlTree(['/reconnecting'], { queryParams: { returnUrl: state.url } });
            }
            // Authenticated with Entra but no usable local profile - the account isn't admin-tier or
            // its /user/me load was forbidden (403). Do NOT auto-redirect to Entra; that's the
            // private-window sign-in LOOP. Send them to a static access-denied page (with Sign Out).
            return router.createUrlTree(['/access-denied']);
        }
        // Not signed in: go to the login landing page with a manual "Sign in" button rather than an
        // automatic redirect, so the browser can never get stuck auto-bouncing to Entra.
        return router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
    }

    router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
    return false;
}